import { useState, type FormEvent } from 'react';
import { joinEventFromWeb, type WebJoinResult } from '../services/portal';

function formatJoinCode(value: string): string {
  const clean = value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 8);
  if (clean.length <= 6) return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
  return `${clean.slice(0, 4)}-${clean.slice(4)}`;
}

type JoinEventPanelProps = {
  userId: string;
  requesterName?: string | null;
  onJoined: (result: WebJoinResult) => void;
};

export function JoinEventPanel({ userId, requesterName, onJoined }: JoinEventPanelProps) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function handleJoin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await joinEventFromWeb({ rawCode: code, userId, requesterName });
      setNotice(result.message);
      onJoined(result);
      if (result.status !== 'requested') setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join this event.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="portal-content-card join-card">
      <div className="portal-section-copy">
        <p className="eyebrow">Join</p>
        <h2>One code. One shared place.</h2>
        <p>Enter the event code from the host or the invite code attached to your invitation.</p>
      </div>

      <form className="join-form" onSubmit={handleJoin}>
        <label htmlFor="join-code">Event or invite code</label>
        <input
          id="join-code"
          value={formatJoinCode(code)}
          onChange={(event) => setCode(event.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 8))}
          placeholder="ABC-123 or ABCD-1234"
          autoComplete="off"
          inputMode="text"
          required
        />
        <button className="primary-button" type="submit" disabled={busy}>{busy ? 'Joining…' : 'Join event'}</button>
      </form>

      <div className="portal-mini-grid">
        <article><strong>6 characters</strong><span>Standard event code</span></article>
        <article><strong>8 characters</strong><span>Direct invite code</span></article>
        <article><strong>Host approval</strong><span>Verified events become a join request</span></article>
      </div>

      {notice && <p className="portal-success">{notice}</p>}
      {error && <p className="error-banner portal-inline-error">{error}</p>}
      <p className="portal-footnote">QR scanning, nearby-event search, and Auto-Gather rules remain available in the mobile app.</p>
    </section>
  );
}
